// src/lib/progressive-loader.ts
// Plain progressive files (mp3/m4a/mp4/…) served from presigned S3/MinIO URLs
// are handed straight to the <video> element: exactly one GET (+ Range GETs
// made by the browser). Shaka is bypassed for them because its src=/MSE
// routing guesses the MIME type with a HEAD request, which a GET-signed URL
// rejects with 403. Manifests (HLS/DASH) still go through Shaka.

import type {
  AssetLoadContext,
  TAsset,
} from "@/hooks/limeplay/use-asset";
import { guessMimeType } from "@/stores/player-types";

type Resolve = (ctx: {
  asset: TAsset;
  signal: AbortSignal;
}) => Promise<string | undefined>;

export function isProgressive(asset: TAsset): boolean {
  const mime = asset.mimeType ?? guessMimeType(asset.src);
  if (!mime) return false;
  return (
    (mime.startsWith("audio/") || mime.startsWith("video/")) &&
    !/mpegurl|dash/i.test(mime)
  );
}

export function createProgressiveLoader(resolve: Resolve) {
  return {
    load: async (ctx: AssetLoadContext) => {
      const { asset, loadDefault, media, player, signal, startTime } = ctx;

      // Not a plain file (or unknown type): keep Limeplay/Shaka behavior.
      if (!isProgressive(asset)) {
        const url = await resolve({ asset, signal });
        await loadDefault(url);
        return;
      }

      const url = await resolve({ asset, signal });
      if (signal.aborted || !url) return;

      // Release whatever Shaka had attached (also clears a previous src).
      await player.unload();
      if (signal.aborted) return;

      await new Promise<void>((resolveLoad, reject) => {
        const cleanup = () => {
          media.removeEventListener("loadedmetadata", onMeta);
          media.removeEventListener("error", onError);
          signal.removeEventListener("abort", onAbort);
        };
        const onMeta = () => {
          cleanup();
          if (startTime && startTime > 0) media.currentTime = startTime;
          resolveLoad();
        };
        const onError = () => {
          cleanup();
          reject(
            new Error(
              `Media failed to load (${media.error?.code ?? "unknown"}): ${
                media.error?.message ?? ""
              }`,
            ),
          );
        };
        const onAbort = () => {
          cleanup();
          resolveLoad(); // superseded by a newer load; caller checks generation
        };

        media.addEventListener("loadedmetadata", onMeta);
        media.addEventListener("error", onError);
        signal.addEventListener("abort", onAbort);

        // "metadata": fetch only headers up front; actual playback then streams
        // with Range requests. "auto" lets Chrome prefetch (nearly) the whole
        // file in the background.
        media.pause();
        media.preload = "metadata";
        media.src = url;
        media.load();
      });
    },
  };
}
